/**
 * SiteDetailModal - Shows site details with tabs for drivers/vehicles/tools
 */
import React, { useState } from 'react';
import { X, MapPin, Users, Car, Hammer, Building2, Settings, Navigation, CalendarDays, ShieldCheck } from 'lucide-react';
import SiteGpsPickerModal from './SiteGpsPickerModal.jsx';
import SitePlanningPanel from './SitePlanningPanel.jsx';
import SiteAccessPanel from './SiteAccessPanel.jsx'; // lot 3.1 : responsables (user_site_access)
import SiteGrantPanel from './SiteGrantPanel.jsx'; // lot 3.2 : gardes et remplacements (site_management_grant)
import { workingSiteAPI } from '../../api';
import { useFormat } from '../../i18n/format.js';

// Status badge config for drivers — le libellé est résolu au rendu (clé i18n), pas ici
const DRIVER_STATUS_CONFIG = {
  active: { bgClass: 'bg-green-100', textClass: 'text-green-800', labelKey: 'cdi' },
  interim: { bgClass: 'bg-blue-100', textClass: 'text-blue-800', labelKey: 'interim' },
  temporary: { bgClass: 'bg-amber-100', textClass: 'text-amber-800', labelKey: 'cdd' },
  cdd: { bgClass: 'bg-amber-100', textClass: 'text-amber-800', labelKey: 'cdd' },
  cdi: { bgClass: 'bg-green-100', textClass: 'text-green-800', labelKey: 'cdi' },
  inactive: { bgClass: 'bg-gray-100', textClass: 'text-gray-800', labelKey: 'inactive' }
};

function getDriverStatusConfig(status) {
  return DRIVER_STATUS_CONFIG[status] || DRIVER_STATUS_CONFIG.inactive;
}

function SiteDetailModal({ site, isAdmin, onClose, onNavigateToAdmin, onSiteUpdated }) {
  const { t, fmt } = useFormat();
  const [activeTab, setActiveTab] = useState('drivers');
  const [showGpsPicker, setShowGpsPicker] = useState(false);

  const hasGps = site.gps_latitude && site.gps_longitude &&
    !isNaN(parseFloat(site.gps_latitude)) && !isNaN(parseFloat(site.gps_longitude));

  const handleGpsSave = async (siteId, lat, lng) => {
    await workingSiteAPI.updateGps(siteId, lat, lng);
    if (onSiteUpdated) onSiteUpdated();
  };

  if (!site) return null;

  const tabs = [
    { id: 'drivers', label: t('sites.tabs.drivers'), count: site.driver_count || 0, Icon: Users },
    { id: 'vehicles', label: t('sites.tabs.vehicles'), count: site.vehicle_count || 0, Icon: Car },
    { id: 'tools', label: t('sites.tabs.tools'), count: site.tool_count || 0, Icon: Hammer },
    { id: 'planning', label: t('sites.tabs.planning'), Icon: CalendarDays },
    { id: 'access', label: t('sites.tabs.responsables'), Icon: ShieldCheck },
    { id: 'info', label: t('sites.tabs.info'), Icon: Building2 }
  ];

  const getInitials = (firstName, lastName) => {
    return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-purple-600 text-white px-6 py-4">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white bg-opacity-20 rounded-lg">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{site.site_name}</h2>
                <p className="text-purple-200">
                  {site.site_code} | {site.address}, {site.postal_code} {site.city}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-white hover:text-purple-200 p-1">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Stats Bar */}
          <div className="flex gap-8 mt-4 pt-4 border-t border-purple-500">
            <div className="text-center">
              <p className="text-3xl font-bold">{site.driver_count || 0}</p>
              <p className="text-sm text-purple-200">{t('sites.stats.drivers')}</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">{site.vehicle_count || 0}</p>
              <p className="text-sm text-purple-200">{t('sites.stats.vehicles')}</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">{site.tool_count || 0}</p>
              <p className="text-sm text-purple-200">{t('sites.stats.tools')}</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm font-medium">
                {t(`sites.types.${site.site_type}`, { defaultValue: site.site_type })}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                site.status === 'active' ? 'bg-green-500' : 'bg-gray-500'
              }`}>
                {site.status === 'active' ? t('sites.status.active') : t('sites.status.inactive')}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex gap-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.Icon className="w-4 h-4" />
                {tab.label} {tab.count !== undefined && `(${tab.count})`}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'drivers' && (
            <div className="space-y-3">
              {(site.drivers || []).length === 0 ? (
                <p className="text-gray-500 text-center py-8">{t('sites.detail.noDrivers')}</p>
              ) : (
                (site.drivers || []).map(driver => {
                  const statusConfig = getDriverStatusConfig(driver.status);
                  return (
                    <div key={driver.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-medium">
                          {getInitials(driver.first_name, driver.last_name)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{driver.first_name} {driver.last_name}</p>
                          <p className="text-sm text-gray-500">{driver.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-2 py-1 ${statusConfig.bgClass} ${statusConfig.textClass} text-xs font-medium rounded-full`}>
                          {t(`sites.driverStatus.${statusConfig.labelKey}`)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {[
                            driver.license_category_b && 'B',
                            driver.license_category_c && 'C',
                            driver.license_category_d && 'D',
                            driver.license_category_ce && 'CE'
                          ].filter(Boolean).join(', ') || '-'}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'vehicles' && (
            <div className="space-y-3">
              {(site.vehicles || []).length === 0 ? (
                <p className="text-gray-500 text-center py-8">{t('sites.detail.noVehicles')}</p>
              ) : (
                (site.vehicles || []).map(vehicle => (
                  <div key={vehicle.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Car className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{vehicle.make} {vehicle.model}</p>
                        <p className="text-sm text-gray-500">{vehicle.license_plate}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500">{vehicle.vehicle_type || '-'}</span>
                      <span className="text-sm text-gray-500">{vehicle.mileage == null ? '-' : t('sites.detail.mileage', { value: fmt.number(vehicle.mileage) })}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'tools' && (
            <div className="space-y-3">
              {(site.tools || []).length === 0 ? (
                <p className="text-gray-500 text-center py-8">{t('sites.detail.noTools')}</p>
              ) : (
                (site.tools || []).map(tool => (
                  <div key={tool.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <Hammer className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{tool.name}</p>
                        <p className="text-sm text-gray-500">{tool.tool_code || tool.serial_number}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500">{tool.category || '-'}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'planning' && (
            <SitePlanningPanel siteId={site.id} />
          )}

          {activeTab === 'access' && (
            <div className="space-y-6">
              <SiteAccessPanel siteId={site.id} />
              <SiteGrantPanel siteId={site.id} />
            </div>
          )}

          {activeTab === 'info' && (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">{t('sites.info.code')}</p>
                <p className="font-medium text-gray-900">{site.site_code}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">{t('sites.info.type')}</p>
                <p className="font-medium text-gray-900">{t(`sites.types.${site.site_type}`, { defaultValue: site.site_type })}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">{t('sites.info.address')}</p>
                <p className="font-medium text-gray-900">{site.address || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">{t('sites.info.city')}</p>
                <p className="font-medium text-gray-900">{site.postal_code} {site.city}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">{t('sites.info.capacity')}</p>
                <p className="font-medium text-gray-900">{site.parking_capacity ? t('sites.card.capacityValue', { count: Number(site.parking_capacity) }) : '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">{t('sites.info.contact')}</p>
                <p className="font-medium text-gray-900">{site.contact_name || '-'}</p>
                <p className="text-sm text-gray-500">{site.contact_phone}</p>
              </div>
              {/* GPS Coordinates */}
              <div className="col-span-2 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">{t('sites.info.gpsCoordinates')}</p>
                    {hasGps ? (
                      <p className="font-mono text-sm font-medium text-gray-900">
                        {parseFloat(site.gps_latitude).toFixed(6)}, {parseFloat(site.gps_longitude).toFixed(6)}
                      </p>
                    ) : (
                      <p className="text-sm text-amber-600">{t('sites.info.gpsUndefined')}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowGpsPicker(true)}
                    className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 flex items-center gap-2 text-sm font-medium border border-purple-200"
                  >
                    <Navigation className="w-4 h-4" />
                    {hasGps ? t('sites.actions.editGps') : t('sites.actions.setGps')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            {site.updated_at && t('sites.detail.updatedAt', { date: fmt.dateTime(site.updated_at) })}
          </p>
          <div className="flex gap-3">
            {isAdmin && (
              <button
                onClick={() => {
                  onClose();
                  onNavigateToAdmin();
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 border border-gray-300"
              >
                <Settings className="w-4 h-4" />
                {t('sites.actions.editAsAdmin')}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              {t('common.actions.close')}
            </button>
          </div>
        </div>

      </div>

      {/* GPS Picker Modal */}
      {showGpsPicker && (
        <SiteGpsPickerModal
          site={site}
          onSave={handleGpsSave}
          onClose={() => setShowGpsPicker(false)}
        />
      )}
    </div>
  );
}

export default SiteDetailModal;
